import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Chip from "@material-ui/core/Chip";
import Typography from "@material-ui/core/Typography";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  tokens: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    margin: theme.spacing(1, 0),
  },
  chip: {
    cursor: "pointer",
    fontFamily: "monospace",
  },
  preview: {
    margin: theme.spacing(1, 0, 2),
    padding: theme.spacing(1),
    borderRadius: 4,
    backgroundColor: theme.palette.action.hover,
    fontFamily: "monospace",
    wordBreak: "break-all",
  },
  previewErro: {
    color: theme.palette.error.main,
  },
  descoberto: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1),
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
  },
}));

/**
 * Prévia gerada pelo SERVIDOR, com a mesma função que monta a URL de verdade.
 * Reimplementar a substituição aqui daria uma prévia que diverge do que será
 * gravado — e o administrador só descobriria ao provisionar a primeira empresa.
 */
const Preview = ({ template, classes }) => {
  const [resultado, setResultado] = useState({ url: null, erro: null });

  useEffect(() => {
    if (!template?.trim()) {
      setResultado({ url: null, erro: null });
      return undefined;
    }
    // Espera o administrador parar de digitar: sem isso é uma chamada por tecla.
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/master/default-plugins/preview", {
          baseUrlTemplate: template,
        });
        setResultado({ url: data.url, erro: null });
      } catch (err) {
        setResultado({
          url: null,
          erro:
            err?.response?.data?.error ||
            i18n.t("defaultPlugins.preview.invalido"),
        });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [template]);

  if (!template?.trim()) return null;

  return (
    <div className={classes.preview}>
      <Typography variant="caption" display="block" color="textSecondary">
        {i18n.t("defaultPlugins.preview.titulo")}
      </Typography>
      {resultado.erro ? (
        <Typography variant="body2" className={classes.previewErro}>
          {i18n.t(`backendErrors.${resultado.erro}`, resultado.erro)}
        </Typography>
      ) : (
        <Typography variant="body2">{resultado.url}</Typography>
      )}
    </div>
  );
};

const DefaultPluginSchema = Yup.object().shape({
  baseUrlTemplate: Yup.string()
    .trim()
    .required(i18n.t("defaultPlugins.modal.erros.obrigatorio")),
});

const initialState = {
  baseUrlTemplate: "",
  active: true,
};

const DefaultPluginModal = ({ open, onClose, plugin }) => {
  const classes = useStyles();
  const [dados, setDados] = useState(initialState);
  const [tokens, setTokens] = useState([]);

  const editando = !!plugin?.id;

  useEffect(() => {
    if (!open) return;
    setDados(
      plugin
        ? {
            baseUrlTemplate: plugin.baseUrlTemplate || "",
            active: plugin.active ?? true,
          }
        : initialState
    );
  }, [open, plugin]);

  useEffect(() => {
    if (!open) return;
    // A lista de tokens vem do servidor: duplicá-la aqui faria a tela oferecer
    // um token que o gerador não conhece (ou esconder um que ele aceita).
    (async () => {
      try {
        const { data } = await api.post("/master/default-plugins/preview", {
          baseUrlTemplate: "http://exemplo/{tenantKey}",
        });
        setTokens(data.tokens || []);
      } catch (err) {
        setTokens([]);
      }
    })();
  }, [open]);

  const handleClose = () => {
    onClose();
    setDados(initialState);
  };

  const handleSave = async (values) => {
    try {
      if (editando) {
        await api.put(`/master/default-plugins/${plugin.id}`, values);
      } else {
        await api.post("/master/default-plugins", values);
      }
      toast.success(i18n.t("defaultPlugins.modal.salvo"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        {editando
          ? i18n.t("defaultPlugins.modal.tituloEditar")
          : i18n.t("defaultPlugins.modal.tituloNovo")}
      </DialogTitle>
      <Formik
        initialValues={dados}
        enableReinitialize
        validationSchema={DefaultPluginSchema}
        onSubmit={async (values, actions) => {
          await handleSave(values);
          actions.setSubmitting(false);
        }}
      >
        {({ values, touched, errors, isSubmitting, setFieldValue }) => (
          <Form>
            <DialogContent dividers>
              <Field
                as={TextField}
                name="baseUrlTemplate"
                label={i18n.t("defaultPlugins.campos.baseUrlTemplate")}
                helperText={
                  (touched.baseUrlTemplate && errors.baseUrlTemplate) ||
                  i18n.t("defaultPlugins.ajuda.baseUrlTemplate")
                }
                error={touched.baseUrlTemplate && Boolean(errors.baseUrlTemplate)}
                variant="outlined"
                margin="dense"
                fullWidth
                autoFocus
              />

              <div className={classes.tokens}>
                {tokens.map((t) => (
                  <Chip
                    key={t.token}
                    size="small"
                    label={t.token}
                    title={t.descricao}
                    onClick={() =>
                      setFieldValue(
                        "baseUrlTemplate",
                        `${values.baseUrlTemplate || ""}${t.token}`
                      )
                    }
                    className={classes.chip}
                  />
                ))}
              </div>

              <Preview template={values.baseUrlTemplate} classes={classes} />

              <FormControlLabel
                control={
                  <Switch
                    checked={values.active}
                    onChange={(e) => setFieldValue("active", e.target.checked)}
                    color="primary"
                  />
                }
                label={i18n.t("defaultPlugins.campos.active")}
              />

              {editando && (
                <div className={classes.descoberto}>
                  <Typography variant="caption" color="textSecondary" display="block">
                    {i18n.t("defaultPlugins.descoberto.titulo")}
                  </Typography>
                  {plugin?.pluginId ? (
                    <Typography variant="body2">
                      {plugin.name || plugin.pluginId}{" "}
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        component="span"
                      >
                        ({plugin.pluginId})
                      </Typography>
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("defaultPlugins.descoberto.pendente")}
                    </Typography>
                  )}
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} color="secondary" disabled={isSubmitting}>
                {i18n.t("defaultPlugins.botoes.cancelar")}
              </Button>
              <div className={classes.btnWrapper}>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {editando
                    ? i18n.t("defaultPlugins.botoes.salvar")
                    : i18n.t("defaultPlugins.botoes.adicionar")}
                </Button>
                {isSubmitting && (
                  <CircularProgress size={24} className={classes.buttonProgress} />
                )}
              </div>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default DefaultPluginModal;
